/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useState, useEffect, useRef, useCallback, ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { fetchAllPages } from "@/lib/fetch-all-pages";
import { useOrganization } from "@/hooks/use-organization";
import { useAuth } from "@/lib/auth";

type AppRole = "owner" | "manager" | "staff" | "viewer";

type StoreOption = { id: string; name: string };

type StoreContextValue = {
  selectedStoreId: string; // "all" or a store UUID
  setSelectedStoreId: (id: string) => void;
  previewRole: AppRole | null;
  setPreviewRole: (role: AppRole | null) => void;
  effectiveRole: AppRole | null;
  availableStores: StoreOption[];
  storesLoading: boolean;
};

const StoreContext = createContext<StoreContextValue | null>(null);

export function StoreContextProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const { organization, role } = useOrganization();
  const [selectedStoreId, setSelectedStoreIdRaw] = useState<string>("all");
  const [previewRole, setPreviewRole] = useState<AppRole | null>(null);
  const lsInitDone = useRef(false);

  const setSelectedStoreId = useCallback((id: string) => {
    if (user?.id) localStorage.setItem(`vri-store:${user.id}`, id);
    setSelectedStoreIdRaw(id);
  }, [user?.id]);

  const effectiveRole = previewRole ?? role;

  // Fetch all stores for owners, assigned stores for others
  const { data: allOrgStores = [], isLoading: allStoresLoading } = useQuery({
    queryKey: ["all-stores", organization?.id],
    queryFn: async () => {
      if (!organization) return [];
      return await fetchAllPages<StoreOption>(() =>
        supabase
          .from("stores")
          .select("id, name")
          .eq("organization_id", organization.id)
          .order("name")
      );
    },
    enabled: !!organization,
  });

  const { data: assignedStoreIds = [] } = useQuery({
    queryKey: ["my-store-assignments", user?.id],
    queryFn: async () => {
      if (!user) return [];
      const data = await fetchAllPages<{ store_id: string }>(() =>
        supabase
          .from("store_assignments")
          .select("store_id")
          .eq("user_id", user.id)
          .order("store_id")
      );
      return data.map((a) => a.store_id);
    },
    enabled: !!user,
  });

  const availableStores =
    role === "owner"
      ? allOrgStores
      : allOrgStores.filter((s) => assignedStoreIds.includes(s.id));

  const storesLoading = !organization || allStoresLoading;

  // Restore selected store from localStorage once user + stores are available
  useEffect(() => {
    if (lsInitDone.current || !user?.id || availableStores.length === 0) return;
    lsInitDone.current = true;
    const saved = localStorage.getItem(`vri-store:${user.id}`);
    if (saved && (saved === "all" || availableStores.some((s) => s.id === saved))) {
      setSelectedStoreIdRaw(saved);
    }
  }, [user?.id, availableStores]);

  // Default to single store for all roles, or first assigned store for non-owners
  useEffect(() => {
    if (!role || availableStores.length === 0 || selectedStoreId !== "all") return;
    if (role !== "owner") {
      setSelectedStoreId(availableStores[0].id);
    } else if (availableStores.length === 1) {
      setSelectedStoreId(availableStores[0].id);
    }
  }, [role, availableStores, selectedStoreId, setSelectedStoreId]);

  // Reset preview when user changes
  useEffect(() => {
    setPreviewRole(null);
  }, [user?.id]);

  return (
    <StoreContext.Provider
      value={{
        selectedStoreId,
        setSelectedStoreId,
        previewRole,
        setPreviewRole,
        effectiveRole,
        availableStores,
        storesLoading,
      }}
    >
      {children}
    </StoreContext.Provider>
  );
}

export function useStoreContext() {
  const ctx = useContext(StoreContext);
  if (!ctx) throw new Error("useStoreContext must be used within StoreContextProvider");
  return ctx;
}
